import type {
    BuildingContext,
    PackActionProposal,
} from '@quipex/shared/contracts';

export type QuippyAssistantAppearance = 'elevated' | 'outlined';

export type QuippyScope =
    | {
          kind: 'building';
          buildingId: string;
      }
    | {
          kind: 'portfolio';
      };

export function sameQuippyScope(a: QuippyScope, b: QuippyScope): boolean {
    return (
        a.kind === b.kind &&
        (a.kind === 'portfolio' ||
            (b.kind === 'building' && a.buildingId === b.buildingId))
    );
}

export type QuippyMessageRole = 'user' | 'assistant';
export type QuippyMessageStatus = 'sent' | 'pending' | 'error';
export type QuippyMessageKind = 'intro' | 'message';

export interface QuippySource {
    id: string | null;
    title: string;
    url: string;
    source: string;
    buildingId?: string;
    buildingName?: string;
}

export type QuippyDocumentSelectionStatus = 'idle' | 'pending' | 'selected';

export interface QuippyDocumentOption {
    fileId: string;
    title: string;
    url?: string;
    buildingId?: string;
    buildingName?: string;
    pageNumbers?: number[];
    excerpt?: string;
}

export interface QuippyMessage {
    id: string;
    kind: QuippyMessageKind;
    role: QuippyMessageRole;
    content: string;
    status: QuippyMessageStatus;
    timestamp: Date;
    sources: QuippySource[];
    packAction?: PackActionProposal;
    documentOptions?: QuippyDocumentOption[];
    selectedDocumentFileId?: string;
    documentSelectionStatus?: QuippyDocumentSelectionStatus;
    /** Original user input — present on error messages to enable retry. */
    retryInput?: string;
    retrySelectedFileId?: string;
}

export interface QuippyThread {
    id: string;
    title: string;
    lastMessage: string;
    timestamp: Date;
    messages: QuippyMessage[];
}

export interface QuippyBuildingSession {
    threads: QuippyThread[];
    draftByChat: Record<string, string>;
    buildingContext?: BuildingContext;
    buildingAddress?: string;
}

export type QuippySessionMap = Map<string, QuippyBuildingSession>;

export interface QuippyChatRef {
    chatId: string;
}

export interface QuippyConversation {
    ref: QuippyChatRef;
    scope: QuippyScope;
    thread: QuippyThread;
    draft: string;
}

export interface QuippyHistoryItem {
    ref: QuippyChatRef;
    scope: QuippyScope;
    title: string;
    lastMessage: string;
    timestamp: Date;
    buildingName?: string;
    hasFailedResponse: boolean;
    hasPendingResponse: boolean;
}

export interface QuippyBuildingDetails {
    name: string;
    address?: string;
}

export interface QuippySession {
    create(scope: QuippyScope): QuippyChatRef;
    read(ref: QuippyChatRef): QuippyConversation | null;
    history(): readonly QuippyHistoryItem[];
    updateBuilding(
        scope: QuippyScope,
        details: QuippyBuildingDetails | null
    ): void;
    buildingDetails(scope: QuippyScope): QuippyBuildingDetails | null;
    setDraft(ref: QuippyChatRef, draft: string): void;
    send(ref: QuippyChatRef, text: string, adminMode?: boolean): void;
    retry(ref: QuippyChatRef, messageId: string, adminMode?: boolean): void;
    edit(
        ref: QuippyChatRef,
        messageId: string,
        text: string,
        adminMode?: boolean
    ): void;
    selectDocument(ref: QuippyChatRef, messageId: string, fileId: string): void;
    delete(ref: QuippyChatRef): void;
    clearAll(): void;
}
